import { formatDateTime, useLocale, useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { Skeleton } from '@multaqa/ui/components/ui/skeleton'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { InlineQueryError } from '@/components/inline-query-error'
import { formatErrorMessage } from '@/lib/error-message'
import type { GroupRow } from '@/lib/platform'
import { useGroupErasure, useRunErasureDryRun } from './api'
import { ErasurePlanView } from './erasure-plan-view'

/**
 * The erasure dry run of a purged group (f418 D6). Shown only on a purged
 * group, the one status the API accepts. The button counts what erasure
 * would delete; nothing is deleted here.
 */
export function ErasurePanel({ group }: { group: GroupRow }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const purged = group.status === 'purged'
  const erasure = useGroupErasure(group.id, purged)
  const dryRun = useRunErasureDryRun(group.id)

  if (!purged) return null

  const plannedAt = erasure.data?.planned_at ?? null

  return (
    <section
      aria-labelledby="group-erasure-heading"
      className="border border-border p-4 space-y-4"
      data-testid="group-erasure-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 id="group-erasure-heading" className="text-h3">
            {t('groups.detail.erasure.title')}
          </h2>
          <p className="text-small text-muted-foreground max-w-prose">
            {t('groups.detail.erasure.description')}
          </p>
          {plannedAt ? (
            <p className="text-small text-muted-foreground" data-testid="group-erasure-planned-at">
              {t('groups.detail.erasure.plannedAt', {
                date: formatDateTime(new Date(plannedAt), locale),
              })}
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          loading={dryRun.isPending}
          disabled={dryRun.isPending}
          onClick={() =>
            dryRun.mutate(undefined, {
              onSuccess: () => toast.success(t('groups.detail.erasure.dryRunDone')),
              onError: (error) => toast.error(formatErrorMessage(error)),
            })
          }
          data-testid="group-erasure-dry-run"
        >
          {plannedAt ? t('groups.detail.erasure.dryRunAgain') : t('groups.detail.erasure.dryRun')}
        </Button>
      </div>
      {erasure.isLoading ? (
        <Skeleton className="h-24 w-full" data-testid="group-erasure-loading" />
      ) : erasure.isError ? (
        <InlineQueryError
          title={t('error.asyncTitle')}
          description={erasure.error.message}
          retryLabel={t('action.retry')}
          onRetry={() => void erasure.refetch()}
        />
      ) : erasure.data?.plan ? (
        <ErasurePlanView plan={erasure.data.plan} />
      ) : (
        <p className="text-small text-muted-foreground" data-testid="group-erasure-no-plan">
          {t('groups.detail.erasure.noPlan')}
        </p>
      )}
    </section>
  )
}
